import { Link } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import React from 'react';

import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  getComparator,
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { EllipsisWithTooltipRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Row, spacing, Text } from '~/src/dash/publicaUI';
import { addCommasToIntegerOrFloat } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { orderDirection } from '~/src/dash/views/platform-connect/common';
import { IForecastingData, IOverlappingLineItems } from '~/src/dash/views/tools/Forecasting/common';

export const formatGroupByDataToLVTRows = (
  data: IForecastingData[],
  summedData: IForecastingData,
  cfg: IForecastingTierTable | IForecastingSiteTable,
  currPage: number,
  rowsPerPage: number,
  orderDirection: Order,
  orderBy: string,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  // use the summed data and push the 'All' row into rows
  // if there are entries in data[0].Sites => Tier table, otherwise Channel table
  if (!isEmpty(data[0]?.Sites) && currPage === 0) {
    const summedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = summedData[responseKey as keyof IForecastingData];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      summedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(summedRow);
  }
  let restOfRows = [] as IListViewTableRow[];
  // insert the rest of the data after the 'All' row
  data.forEach((datum, index) => {
    if (restOfRows.length >= rowsPerPage) {
      return;
    }
    if ((!isEmpty(data[0]?.Sites) ? index + 1 : index) < currPage * rowsPerPage) {
      return;
    }
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IForecastingData];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    restOfRows.push(row);
  });
  restOfRows = stableSort(
    restOfRows,
    getComparator(orderDirection, orderBy, cfg?.columns[orderBy]?.valueType),
  ) as IListViewTableRow[];
  return rows.concat(restOfRows);
};

export const formatLineItemDataToLVTRows = (
  data: IOverlappingLineItems[],
  cfg: IForecastingLineItemTable,
  currPage: number,
  rowsPerPage: number,
  orderDirection: Order,
  orderBy: string,
): IListViewTableRow[] => {
  let rows = [] as IListViewTableRow[];
  // early return to prevent error when initially rendering component
  if (isEmpty(data)) {
    return rows;
  }
  const sortedData = [...data];
  if (typeof data[0][orderBy as keyof IOverlappingLineItems] === 'string') {
    sortedData.sort((a: IOverlappingLineItems, b: IOverlappingLineItems) => {
      const aVal = ((a[orderBy as keyof IOverlappingLineItems] ?? '') as string).trim();
      const bVal = ((b[orderBy as keyof IOverlappingLineItems] ?? '') as string).trim();
      return orderDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  } else {
    // number type
    sortedData.sort((a: IOverlappingLineItems, b: IOverlappingLineItems) => {
      const aVal = a[orderBy as keyof IOverlappingLineItems] as number;
      const bVal = b[orderBy as keyof IOverlappingLineItems] as number;
      return orderDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }
  sortedData.forEach((datum, index) => {
    if (rows.length >= rowsPerPage) {
      return;
    }
    if (index < currPage * rowsPerPage) {
      return;
    }
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IOverlappingLineItems];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

export const formatSitesModalDataToLVTRows = (
  data: ISmallSite[],
  cfg: IForecastingTierTable | IForecastingSiteTable | IForecastingSiteModalTable,
  currPage: number,
  rowsPerPage: number,
  orderDirection: Order,
  orderBy: string,
): IListViewTableRow[] => {
  let rows = [] as IListViewTableRow[];
  // early return to prevent error when initially rendering component
  if (isEmpty(data)) {
    return rows;
  }
  const sortedData = [...data];
  if (typeof data[0][orderBy as keyof ISmallSite] === 'string') {
    sortedData.sort((a: ISmallSite, b: ISmallSite) => {
      const aVal = ((a[orderBy as keyof ISmallSite] ?? '') as string).trim();
      const bVal = ((b[orderBy as keyof ISmallSite] ?? '') as string).trim();
      return orderDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }
  sortedData.forEach((datum, index) => {
    if (rows.length >= rowsPerPage) {
      return;
    }
    if (index < currPage * rowsPerPage) {
      return;
    }
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof ISmallSite];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

// bolds the 'All' row in the tier tables
const boldFirstCellEntry = (props: ICustomRenderCellJSXProps): JSX.Element => {
  return (
    <Row extraSmallVerticalPadding noHorizontalPadding>
      <Text small bold={props.row.Name.value === 'All'} color={publicaColors.black}>
        {props.value}
      </Text>
    </Row>
  );
};

// bolds the 'All' row in the tier tables
const boldFirstCellEntryNumbers = (props: ICustomRenderCellJSXProps): JSX.Element => {
  let formattedValue = props.value;
  // add commas to cell values that are numbers if needed
  if (formattedValue) {
    if (typeof props.value === 'number') {
      formattedValue = String(Math.round(props.value));
    }
  }
  const value = addCommasToIntegerOrFloat((formattedValue as string) || '0');
  return (
    <Row extraSmallVerticalPadding noHorizontalPadding>
      <Text small bold={props.row.Name.value === 'All'} color={publicaColors.black}>
        {value}
      </Text>
    </Row>
  );
};

const formatValue = (val: string | number) => {
  let formattedValue = val;
  // add commas to cell values that are numbers if needed
  if (formattedValue) {
    if (typeof val === 'number') {
      formattedValue = String(Math.round(val));
    }
  }

  return addCommasToIntegerOrFloat((formattedValue as string) || '0');
};

const siteModalRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  return (
    <Row extraSmallVerticalPadding>
      <div
        onClick={() => customFunctions.handleOpenSiteModal(props)}
        style={{
          cursor: 'pointer',
        }}
      >
        {props?.row?.Sites?.value?.length || ''}
      </div>
    </Row>
  );
};

const lineItemNameRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  return (
    <Link
      target='_blank'
      style={{
        textDecoration: 'none',
        color: publicaColors.blue,
      }}
      href={'#' + customFunctions.getLineItemRoute(props)}
    >
      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
        <Col>{props.value}</Col>
        <Col displayFlex>
          <IconV2 color={publicaColors.blue} name='externalLink' />
        </Col>
      </Cols>
    </Link>
  );
};

const flightDatesRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  return (
    <div>
      {(props.value as string[]).map((val) => {
        return <Text small>{val}</Text>;
      })}
    </div>
  );
};

const DeliveryPercentageRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  return <Text small>{`${props.value}%`}</Text>;
};

const convertedCurrencyRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { customFunctions } = useListViewTableStore();
  return (
    <Row extraSmallVerticalPadding noHorizontalPadding>
      <Text small bold={props.row.Name.value === 'All'} color={publicaColors.black}>
        {customFunctions.convertToPublisherCurrencyValue(props.value)}
      </Text>
    </Row>
  );
};

const convertedCurrencyRoundedRender = (props: ICustomRenderCellJSXProps): JSX.Element => {
  const { customFunctions } = useListViewTableStore();
  return (
    <Row extraSmallVerticalPadding noHorizontalPadding>
      <Text small bold={props.row.Name.value === 'All'} color={publicaColors.black}>
        {customFunctions.convertToPublisherCurrencyValueRounded(props.value)}
      </Text>
    </Row>
  );
};

interface IForecastingTierTable {
  defaultState: {
    tierActiveColumnKeys: string[];
    tierActiveColumnKeysWithoutBookedImps: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columns: IListViewTableColumns;
}

interface IForecastingSiteTable {
  defaultState: {
    siteActiveColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columns: IListViewTableColumns;
}

interface IForecastingLineItemTable {
  defaultState: {
    lineItemActiveColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columns: IListViewTableColumns;
}

interface IForecastingSiteModalTable {
  defaultState: {
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columns: IListViewTableColumns;
}

export interface IForecastingDataConfig {
  cypressClassName: string;
  tierTable: IForecastingTierTable;
  siteTable: IForecastingSiteTable;
  lineItemTable: IForecastingLineItemTable;
  siteModalTable: IForecastingSiteModalTable;
}

export const forecastingDataConfig = {
  cypressClassName: 'Forecasting',
  tierTable: {
    defaultState: {
      tierActiveColumnKeys: [
        'Sites',
        'TotalAvails',
        'RenderableAvails',
        'ExpectedImps',
        'AvgCPM',
        'ExpectedRevenue',
        'BookedImps',
      ],
      tierActiveColumnKeysWithoutBookedImps: [
        'Sites',
        'TotalAvails',
        'ExpectedImps',
        'AvgCPM',
        'ExpectedRevenue',
      ],
      isTableExpanded: false,
      orderDirection: 'asc',
      orderBy: 'Name',
      page: 0,
      rowsPerPage: 10,
    },
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        formatValue: (value: string) => (isNaN(Number(value)) ? value : Number(value)),
        cellJSX: boldFirstCellEntry,
        cellStyle: {
          fontWeight: FONT_WEIGHT.base,
        },
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Tier',
        requestKey: '',
        responseKey: 'Tier',
        valueType: 'number',
        orderByKey: 'Name',
      },
      Sites: {
        cellJSX: siteModalRender,
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Sites',
        label: 'Channels',
        requestKey: '',
        responseKey: 'Sites',
        valueType: 'number',
        orderByKey: 'Sites',
      },
      TotalAvails: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        headerStyle: {
          minWidth: '176px',
        },
        isSearchable: true,
        key: 'TotalAvails',
        label: 'Total Available Slots',
        requestKey: '',
        responseKey: 'Avails',
        valueType: 'number',
        orderByKey: 'TotalAvails',
      },
      RenderableAvails: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'RenderableAvails',
        label: 'Renderable Slots',
        requestKey: '',
        responseKey: 'RenderableAvails',
        valueType: 'number',
        orderByKey: 'RenderableAvails',
      },
      ExpectedImps: {
        cellJSX: boldFirstCellEntryNumbers,
        cellStyle: {
          paddingTop: '12px',
          paddingBottom: '4px',
        },
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ExpectedImps',
        label: 'Expected Imps',
        requestKey: '',
        responseKey: 'ExpectedImps',
        valueType: 'number',
        orderByKey: 'ExpectedImps',
      },
      AvgCPM: {
        cellJSX: convertedCurrencyRender,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'AvgCPM',
        label: 'Avg. CPM',
        requestKey: '',
        responseKey: 'AvgCPM',
        valueType: 'number',
        orderByKey: 'AvgCPM',
      },
      ExpectedRevenue: {
        cellJSX: convertedCurrencyRoundedRender,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ExpectedRevenue',
        label: 'Expected Revenue',
        requestKey: '',
        responseKey: 'ExpectedRevenue',
        valueType: 'number',
        orderByKey: 'ExpectedRevenue',
      },
      BookedImps: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'BookedImps',
        label: 'Booked Imps',
        requestKey: '',
        responseKey: 'BookedImps',
        valueType: 'number',
        orderByKey: 'BookedImps',
      },
    },
  },
  siteTable: {
    defaultState: {
      siteActiveColumnKeys: [
        'Tier',
        'TotalAvails',
        'RenderableAvails',
        'ExpectedImps',
        'AvgCPM',
        'ExpectedRevenue',
      ],
      isTableExpanded: false,
      orderDirection: 'asc',
      orderBy: 'TotalAvails',
      page: 0,
      rowsPerPage: 10,
    },
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: boldFirstCellEntry,
        cellStyle: {
          fontWeight: FONT_WEIGHT.base,
        },
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Channels',
        requestKey: '',
        responseKey: 'SiteName',
        valueType: 'string',
        orderByKey: 'Name',
      },
      Tier: {
        formatValue: (value: number) => String(value),
        cellJSX: boldFirstCellEntry,
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Tier',
        label: 'Tier',
        requestKey: '',
        responseKey: 'Tier',
        valueType: 'number',
        disableSort: true,
      },
      TotalAvails: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        headerStyle: {
          minWidth: '176px',
        },
        isSearchable: true,
        key: 'TotalAvails',
        label: 'Total Available Slots',
        requestKey: '',
        responseKey: 'Avails',
        valueType: 'number',
        orderByKey: 'TotalAvails',
      },
      RenderableAvails: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'RenderableAvails',
        label: 'Renderable Slots',
        requestKey: '',
        responseKey: 'RenderableAvails',
        valueType: 'number',
        orderByKey: 'RenderableAvails',
      },
      ExpectedImps: {
        cellJSX: boldFirstCellEntryNumbers,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ExpectedImps',
        label: 'Expected Imps',
        requestKey: '',
        responseKey: 'ExpectedImps',
        valueType: 'number',
        orderByKey: 'ExpectedImps',
      },
      AvgCPM: {
        cellJSX: convertedCurrencyRender,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'AvgCPM',
        label: 'Avg. CPM',
        requestKey: '',
        responseKey: 'AvgCPM',
        valueType: 'number',
        orderByKey: 'AvgCPM',
      },
      ExpectedRevenue: {
        cellJSX: convertedCurrencyRoundedRender,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ExpectedRevenue',
        label: 'Expected Revenue',
        requestKey: '',
        responseKey: 'ExpectedRevenue',
        valueType: 'number',
        orderByKey: 'ExpectedRevenue',
      },
    },
  },
  lineItemTable: {
    defaultState: {
      lineItemActiveColumnKeys: [
        'Name',
        'Tier',
        'ImpressionGoal',
        'DeliveryPercentage',
        'FlightDates',
        'NetPrice',
      ],
      isTableExpanded: false,
      orderDirection: 'asc',
      orderBy: 'Tier',
      page: 0,
      rowsPerPage: 10,
    },
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: lineItemNameRender,
        cellStyle: {
          fontWeight: FONT_WEIGHT.base,
        },
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        cellRenderType: 'name_link',
        isSearchable: true,
        key: 'Name',
        label: 'Line Item',
        requestKey: '',
        responseKey: 'Name',
        valueType: 'string',
        orderByKey: 'Name',
      },
      Tier: {
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'Tier',
        label: 'Tier',
        requestKey: '',
        responseKey: 'Tier',
        valueType: 'number',
        orderByKey: 'Tier',
      },
      ImpressionGoal: {
        formatValue,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ImpressionGoal',
        label: 'Impression Goal',
        requestKey: '',
        responseKey: 'ImpressionGoal',
        valueType: 'number',
        orderByKey: 'ImpressionGoal',
      },
      DeliveryPercentage: {
        cellJSX: DeliveryPercentageRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        headerStyle: {
          minWidth: '172px',
        },
        isSearchable: true,
        key: 'DeliveryPercentage',
        label: 'Delivery %',
        requestKey: '',
        responseKey: 'DeliveryPercentage',
        valueType: 'number',
        orderByKey: 'DeliveryPercentage',
      },
      FlightDates: {
        cellJSX: flightDatesRender,
        isSearchable: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'FlightDates',
        label: 'Flight Dates',
        requestKey: '',
        responseKey: 'FlightDates',
        valueType: 'number',
        orderByKey: 'FlightDates',
      },
      NetPrice: {
        cellJSX: convertedCurrencyRender,
        headerRenderType: 'number',
        isSearchable: true,
        key: 'NetPrice',
        label: 'Net Price',
        requestKey: '',
        responseKey: 'NetPrice',
        valueType: 'number',
        orderByKey: 'NetPrice',
      },
      OrderID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'OrderID',
        label: 'OrderID',
        requestKey: 'OrderID',
        responseKey: 'OrderID',
        valueType: 'string',
      },
    },
  },
  siteModalTable: {
    defaultState: {
      isTableExpanded: false,
      orderDirection: 'asc',
      orderBy: 'Name',
      page: 0,
      rowsPerPage: 10,
    },
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        cellStyle: {
          padding: '4px 0px',
        },
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
        orderByKey: 'Name',
      },
    },
  },
} as IForecastingDataConfig;
